import type { EventProfile, EventStallMenuItem, EventStallOperator, IntegrationSettings } from '../types';
import { sanitizeEventTokenRollMax } from './eventOrderTokens';

/**
 * Canonical list of Event-only Smartpay terminal ids. Merges `eventTerminalIds` with legacy `eventTerminalId`.
 */
export function collectEventTerminalIdsFromStall(
  stall: IntegrationSettings['eventStall'] | undefined,
): string[] {
  const raw = stall?.eventTerminalIds;
  const legacy = stall?.eventTerminalId?.trim();
  const fromArray = Array.isArray(raw) ? raw.map((s) => String(s ?? '').trim()).filter(Boolean) : [];
  const merged = fromArray.length > 0 ? fromArray : legacy ? [legacy] : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of merged) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/** Shallow clone event menu rows for safe mutations. */
export function cloneEventMenuItems(items: EventStallMenuItem[] | undefined): EventStallMenuItem[] {
  return (items ?? []).map((row) => ({ ...row }));
}

export function sanitizeEventMenuItems(items: EventStallMenuItem[] | undefined): EventStallMenuItem[] {
  if (!Array.isArray(items)) return [];
  return items
    .map((item, index) => {
      const id = Number((item as EventStallMenuItem).id);
      const name = String((item as EventStallMenuItem).name ?? '').trim();
      const categoryName = String((item as EventStallMenuItem).categoryName ?? '').trim();
      const price = Number((item as EventStallMenuItem).price);
      const rawOpeningQty = (item as EventStallMenuItem).openingQuantity;
      const openingQuantity =
        rawOpeningQty == null || (typeof rawOpeningQty === 'string' && rawOpeningQty.trim() === '')
          ? undefined
          : Math.max(0, Math.floor(Number(rawOpeningQty)));
      if (!Number.isFinite(id) || !name || !categoryName || !Number.isFinite(price) || price <= 0) return null;
      const rawCost = (item as EventStallMenuItem).unitCost;
      const unitCost =
        rawCost == null || (typeof rawCost === 'string' && String(rawCost).trim() === '')
          ? undefined
          : Math.round(Number(rawCost) * 100) / 100;
      const safeCost = unitCost != null && Number.isFinite(unitCost) && unitCost >= 0 ? unitCost : undefined;

      return {
        id,
        name,
        categoryName,
        price: Math.round(price * 100) / 100,
        imageUrl: String((item as EventStallMenuItem).imageUrl ?? '').trim() || undefined,
        isAvailable: (item as EventStallMenuItem).isAvailable ?? true,
        soldOut: !!(item as EventStallMenuItem).soldOut,
        openingQuantity: openingQuantity != null && Number.isFinite(openingQuantity) ? openingQuantity : undefined,
        displayOrder: Number((item as EventStallMenuItem).displayOrder ?? index),
        ...(safeCost !== undefined ? { unitCost: safeCost } : {}),
      };
    })
    .filter((item): item is EventStallMenuItem => item !== null);
}

function sanitizeEventOperators(ops: EventStallOperator[] | undefined): EventStallOperator[] {
  if (!Array.isArray(ops)) return [];
  return ops
    .map((o) => ({
      id: String(o?.id ?? '').trim(),
      name: String(o?.name ?? '').trim(),
      pinSalt: String(o?.pinSalt ?? ''),
      pinHash: String(o?.pinHash ?? ''),
    }))
    .filter((o) => o.id && o.name && o.pinSalt && o.pinHash);
}

function seedLegacyEventProfile(settings: IntegrationSettings): EventProfile {
  const now = Date.now();
  const menu = sanitizeEventMenuItems(settings.eventStall?.eventMenuItems);
  const categoryOrder = Array.isArray(settings.eventStall?.eventCategoryOrder)
    ? settings.eventStall!.eventCategoryOrder!.map((s) => String(s ?? '').trim()).filter(Boolean)
    : [];
  return {
    id: `evt-legacy-${now}`,
    name: String(settings.eventStall?.name ?? '').trim() || 'Legacy Event',
    status: 'active',
    createdAt: now,
    menuSnapshot: menu,
    eventCategoryOrderSnapshot: categoryOrder,
  };
}

function sanitizeEventProfiles(events: EventProfile[] | undefined, fallbackSettings: IntegrationSettings): EventProfile[] {
  if (!Array.isArray(events) || events.length === 0) return [seedLegacyEventProfile(fallbackSettings)];
  const clean = events
    .map((evt, idx) => {
      const id = String(evt?.id ?? '').trim();
      const name = String(evt?.name ?? '').trim();
      const status = evt?.status === 'archived' ? 'archived' : 'active';
      const createdAt = Number(evt?.createdAt);
      const archivedAt = evt?.archivedAt != null ? Number(evt.archivedAt) : undefined;
      const menuSnapshot = sanitizeEventMenuItems(evt?.menuSnapshot);
      const eventCategoryOrderSnapshot = Array.isArray(evt?.eventCategoryOrderSnapshot)
        ? evt!.eventCategoryOrderSnapshot!.map((s) => String(s ?? '').trim()).filter(Boolean)
        : [];
      if (!id || !name || !Number.isFinite(createdAt)) {
        const fallback = seedLegacyEventProfile(fallbackSettings);
        return { ...fallback, id: `evt-recovered-${Date.now()}-${idx}` };
      }
      return {
        id,
        name,
        status,
        createdAt,
        archivedAt: archivedAt != null && Number.isFinite(archivedAt) ? archivedAt : undefined,
        menuSnapshot,
        eventCategoryOrderSnapshot,
        notes: evt?.notes ? String(evt.notes) : undefined,
      } as EventProfile;
    })
    .filter((evt) => evt.menuSnapshot.length > 0 || evt.status === 'archived');
  return clean.length > 0 ? clean : [seedLegacyEventProfile(fallbackSettings)];
}

/**
 * Canonical Event/Stall shape: mirrors Settings → ensureSettingsDefaults eventStall block.
 * Aligns flat `eventMenuItems` with the active profile's `menuSnapshot`.
 */
export function normalizeEventStallSection(settings: IntegrationSettings): NonNullable<IntegrationSettings['eventStall']> {
  const menu = sanitizeEventMenuItems(settings.eventStall?.eventMenuItems);
  const order = Array.isArray(settings.eventStall?.eventCategoryOrder)
    ? settings.eventStall!.eventCategoryOrder!.map((s) => String(s ?? '').trim()).filter(Boolean)
    : [];
  const normalizedSettings = {
    ...settings,
    eventStall: {
      ...settings.eventStall,
      eventMenuItems: menu,
      eventCategoryOrder: order,
    },
  } as IntegrationSettings;
  const events = sanitizeEventProfiles(settings.eventStall?.events, normalizedSettings);
  const requestedActiveId = settings.eventStall?.activeEventId;
  const activeEvent =
    events.find((e) => e.id === requestedActiveId) ?? events.find((e) => e.status === 'active') ?? events[0];
  const activeMenu = activeEvent ? cloneEventMenuItems(activeEvent.menuSnapshot) : menu;
  const activeOrder = activeEvent?.eventCategoryOrderSnapshot ?? order;
  const eventTerminalIds = collectEventTerminalIdsFromStall(settings.eventStall);
  const eventTerminalId = eventTerminalIds[0];
  const rawRoll = settings.eventStall?.eventTokenRollMax;
  let eventTokenRollMaxOut: number | undefined;
  if (rawRoll === 0 || rawRoll === null) {
    eventTokenRollMaxOut = 0;
  } else {
    const s = sanitizeEventTokenRollMax(rawRoll);
    if (s !== undefined) eventTokenRollMaxOut = s;
  }
  return {
    enabled: settings.eventStall?.enabled ?? false,
    name: activeEvent?.name ?? settings.eventStall?.name ?? '',
    eventMenuItems: activeMenu,
    events,
    activeEventId: activeEvent?.id,
    eventCategoryOrder: activeOrder,
    menuItemIds: settings.eventStall?.menuItemIds ?? [],
    tokenCounterResetAt: settings.eventStall?.tokenCounterResetAt,
    ...(eventTokenRollMaxOut !== undefined ? { eventTokenRollMax: eventTokenRollMaxOut } : {}),
    eventTerminalIds,
    ...(eventTerminalId ? { eventTerminalId } : {}),
    eventOperators: sanitizeEventOperators(settings.eventStall?.eventOperators),
  };
}

/**
 * Merged unit costs for reporting: every event profile snapshot fills gaps; flat `eventMenuItems` wins on conflicts.
 */
export function buildEventStallUnitCostMap(settings: IntegrationSettings | null | undefined): Map<number, number> {
  const m = new Map<number, number>();
  const stall = settings?.eventStall;
  if (!stall) return m;

  for (const evt of stall.events ?? []) {
    for (const row of evt.menuSnapshot ?? []) {
      const id = Number(row.id);
      const c = row.unitCost;
      if (!Number.isFinite(id) || m.has(id)) continue;
      if (c != null && Number.isFinite(Number(c)) && Number(c) >= 0) {
        m.set(id, Math.round(Number(c) * 100) / 100);
      }
    }
  }

  for (const row of stall.eventMenuItems ?? []) {
    const id = Number(row.id);
    const c = row.unitCost;
    if (Number.isFinite(id) && c != null && Number.isFinite(Number(c)) && Number(c) >= 0) {
      m.set(id, Math.round(Number(c) * 100) / 100);
    }
  }

  return m;
}
